// Allows you to add a link to the text when you press CMD+K
// https://medium.com/@siobhanpmahoney/building-a-rich-text-editor-with-react-and-draft-js-part-2-2-embedding-links-d71b57d187a7
import React from 'react';
import {
  RichUtils,
  KeyBindingUtil,
  EditorState
  // CompositeDecorator
} from 'draft-js';
import { childrenType, contentStateType, entityKeyType } from '../../../types';

export const linkStrategy = (contentBlock, callback, contentState) => {
  contentBlock.findEntityRanges(character => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === 'LINK'
    );
  }, callback);
};

// Only allow safe URL schemes. A user-authored LINK entity is rendered in the
// read-only view other users see, and React does NOT strip a `javascript:` (or
// `data:`) href — so an unvalidated href is a stored-XSS vector. Returns a safe
// URL string, or null if the scheme is disallowed (then we render plain text).
const sanitizeUrl = rawUrl => {
  if (!rawUrl) {
    return null;
  }
  // Drop control characters (incl. tab/newline/return) that could smuggle a
  // scheme past the check, e.g. "java\tscript:alert(1)". Done via char codes
  // rather than a control-char regex literal to keep the source clean.
  const cleaned = String(rawUrl)
    .split('')
    .filter(ch => {
      const code = ch.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('')
    .trim();
  const schemeMatch = cleaned.match(/^([a-z][a-z0-9+.-]*):/i);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (scheme !== 'http' && scheme !== 'https' && scheme !== 'mailto') {
      return null; // javascript:, data:, vbscript:, file:, etc.
    }
  }
  // no scheme (relative / bare domain) can't execute script — allow it
  return cleaned;
};

export const Link = props => {
  const { contentState, entityKey } = props;
  const { url } = contentState.getEntity(entityKey).getData();
  const safeUrl = sanitizeUrl(url);
  if (!safeUrl) {
    // disallowed scheme — show the text but don't make it a live link
    return <span className="link">{props.children}</span>;
  }
  return (
    <a
      className="link"
      href={safeUrl}
      rel="noopener noreferrer"
      target="_blank"
      aria-label={safeUrl}
    >
      {props.children}
    </a>
  );
};

const addLinkPluginPlugin = {
  keyBindingFn(event, { getEditorState }) {
    const editorState = getEditorState();
    const selection = editorState.getSelection();
    if (selection.isCollapsed()) {
      return null;
    }
    if (KeyBindingUtil.hasCommandModifier(event) && event.which === 75) {
      return 'add-link';
    }
    return null;
  },

  // handleKeyCommand(command, editorState, { getEditorState, setEditorState }) {
  handleKeyCommand(command, editorState, { setEditorState }) {
    if (command !== 'add-link') {
      return 'not-handled';
    }
    const link = window.prompt('Paste the link -');
    const selection = editorState.getSelection();
    if (!link) {
      setEditorState(RichUtils.toggleLink(editorState, selection, null));
      return 'handled';
    }
    const content = editorState.getCurrentContent();
    const contentWithEntity = content.createEntity('LINK', 'MUTABLE', {
      url: link
    });
    const newEditorState = EditorState.push(
      editorState,
      contentWithEntity,
      'create-entity'
    );
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    setEditorState(RichUtils.toggleLink(newEditorState, selection, entityKey));
    return 'handled';
  },

  decorators: [
    {
      strategy: linkStrategy,
      component: Link
    }
  ]
};

Link.propTypes = {
  children: childrenType.isRequired,
  contentState: contentStateType.isRequired,
  entityKey: entityKeyType.isRequired
};

export default addLinkPluginPlugin;
